import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import type { GameEvent } from '../../core/events/GameEvents';

/**
 * Dialog for responding to a staff demand.
 * Shows the staff member's request with Accept/Negotiate/Deny options.
 */
export function openDemandDialog(parent: HTMLElement, store: GameStore, event: GameEvent, onClose: () => void): void {
  if (event.type !== 'STAFF_DEMAND') return;

  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const demandDescriptions: Record<string, string> = {
    raise: `wants a raise`,
    better_office: `wants better office facilities`,
    less_workload: `is feeling burned out and needs time off`,
    promotion: `thinks they've earned a promotion`,
    new_equipment: `needs new equipment`,
  };

  const desc = demandDescriptions[event.demand.type] ?? 'has a request';

  const dialog = new Dialog({
    title: 'Staff Request',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
    actions: [
      {
        label: 'Accept',
        style: 'success',
        onClick: () => {
          store.dispatch({ type: 'RESPOND_TO_DEMAND', staffId: event.staffId, response: 'accept' });
          dialog.close();
        },
      },
      {
        label: 'Negotiate',
        onClick: () => {
          store.dispatch({ type: 'RESPOND_TO_DEMAND', staffId: event.staffId, response: 'negotiate' });
          dialog.close();
        },
      },
      {
        label: 'Deny',
        style: 'danger',
        onClick: () => {
          store.dispatch({ type: 'RESPOND_TO_DEMAND', staffId: event.staffId, response: 'deny' });
          dialog.close();
        },
      },
    ],
  });

  const content = document.createElement('div');
  content.style.cssText = 'display:flex;flex-direction:column;gap:12px;min-width:280px;';

  const nameEl = document.createElement('div');
  nameEl.style.cssText = 'font-size:16px;font-weight:600;';
  nameEl.textContent = event.name;
  content.appendChild(nameEl);

  const descEl = document.createElement('div');
  descEl.style.cssText = 'color:var(--color-text-secondary);font-size:13px;';
  descEl.textContent = `"${event.name} ${desc}."`;
  content.appendChild(descEl);

  const infoEl = document.createElement('div');
  infoEl.style.cssText = 'font-size:11px;color:var(--color-text-muted);border-top:1px solid var(--border-color);padding-top:8px;';
  infoEl.innerHTML = `
    <strong>Accept:</strong> Full cost, morale restored to 70<br>
    <strong>Negotiate:</strong> Half cost, morale to 50, may follow up<br>
    <strong>Deny:</strong> No cost, morale drops, may leave
  `;
  content.appendChild(infoEl);

  dialog.body.appendChild(content);
  dialog.mount(parent);
}
